import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Play, Users, GraduationCap, Mic2, QrCode, UtensilsCrossed, Heart, Instagram, Mail, CalendarHeart } from 'lucide-react';
import { useLang, LANGS } from '../lib/i18n';
import { investorCopy } from '../lib/investor-copy';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import BusinessStory from '../components/BusinessStory';
import '../InvestorLanding.css';

const EMAIL = 'aaronrutigliano@icloud.com';
const NAV_IDS = ['system', 'qr', 'business', 'pilot'];
const MODULE_ICONS = [Users, GraduationCap, Mic2, QrCode];
const VIDEO = '/media/buskers-concept.mp4';
const POSTER = '/media/buskers-concept-poster.jpg';
const mail = (subject) => `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}`;

function ConceptFilm({ c }) {
  const video = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);
  const play = () => {
    setStarted(true);
    requestAnimationFrame(() => {
      video.current?.focus();
      video.current?.play().catch(() => {});
    });
  };
  return <figure className="investor-film" id="film">
    <div className="film-screen">
      {started ? <video ref={video} src={VIDEO} poster={POSTER} controls playsInline preload="metadata" tabIndex={0} aria-label={c.videoLabel} onError={() => setFailed(true)} /> :
        <Button variant="ghost" className="film-play" onClick={play} aria-label={c.videoLabel}>
          <img src={POSTER} alt="" width="1126" height="720" fetchPriority="high" />
          <span className="film-shade" />
          <span className="film-play-circle"><Play size={26} fill="currentColor" aria-hidden="true" /></span>
          <span className="film-caption"><strong>{c.watch}</strong><span>{c.duration}</span></span>
        </Button>}
      {failed && <p className="film-error">{c.videoError} <a href={VIDEO}>{c.videoDownload}</a></p>}
    </div>
    <figcaption>{c.videoNote}</figcaption>
    <Accordion type="single" collapsible className="film-summary">
      <AccordionItem value="summary"><AccordionTrigger>{c.summary}</AccordionTrigger><AccordionContent>{c.filmSummary}</AccordionContent></AccordionItem>
    </Accordion>
  </figure>;
}

function TalentJourney({ c }) {
  const photos = [
    '/images/business/talent-transformations.jpeg',
    '/images/business/social-invitation.jpg',
    '/images/business/academy-training.png',
    '/images/business/serve-and-perform.png',
    '/images/business/scan-to-order.jpg',
    '/images/business/artist-profile.jpg',
  ];
  return <section className="investor-talent-journey investor-shell" id="talent" aria-labelledby="talent-title">
    <div className="investor-section-head"><div><p className="investor-eyebrow">{c.talentEyebrow}</p><h2 id="talent-title">{c.talentTitle}</h2></div><p>{c.talentIntro}</p></div>
    <ol className="talent-journey-grid">
      {c.talentSteps.map((step, i) => <li key={step.title}>
        <article className="talent-journey-card" aria-labelledby={`talent-step-${i + 1}`}>
          <div className={`talent-journey-photo ${i === 0 ? 'talent-journey-opening' : ''}`}>
            {i === 0 ? <>
              <div className="journey-creator-crop"><img src={photos[0]} alt={step.alt} width="1600" height="1600" loading="lazy" decoding="async" /></div>
              <img className="journey-street-photo" src="/images/business/street-juggler.jpg" alt={c.streetAlt} width="1126" height="720" loading="lazy" decoding="async" />
            </> : <img src={photos[i]} alt={step.alt} width={i === 2 ? 941 : i === 3 ? 1122 : 1126} height={i === 2 ? 1672 : i === 3 ? 1402 : 720} loading="lazy" decoding="async" className={i === 2 ? 'journey-academy-photo' : ''} />}
          </div>
          <div className="talent-journey-text">
            <div className="talent-step-heading"><span className="talent-step-number">0{i + 1}</span><h3 id={`talent-step-${i + 1}`}>{step.title}</h3></div>
            <p>{step.text}</p>
            <span className="talent-step-caption">{step.caption}</span>
          </div>
        </article>
      </li>)}
    </ol>
    <div className="talent-journey-footer"><p className="investor-note">{c.talentNote}</p><Link className="investor-text-link" to="/a/a1">{c.guestDemo}<ArrowUpRight size={18} aria-hidden="true" /></Link></div>
  </section>;
}

export default function InvestorLanding() {
  const { lang, setLang } = useLang();
  const c = investorCopy[lang] || investorCopy.en;
  useEffect(() => { document.title = `Buskers on the Table — ${c.eyebrow}`; }, [c.eyebrow]);
  return <div className="investor-page" id="top">
    <a href="#main" className="investor-skip">{c.skip}</a>
    <header className="investor-header">
      <div className="investor-shell investor-header-inner">
        <a className="investor-brand" href="#top" aria-label="Buskers on the Table">
          <img src="/images/5ff2283bf031.png" alt="" width="48" height="48" />
          <span>BUSKERS<small>ON THE TABLE</small></span>
        </a>
        <nav className="investor-nav" aria-label={lang === 'it' ? 'Navigazione principale' : lang === 'es' ? 'Navegación principal' : 'Main navigation'}>
          {c.nav.map((label, i) => <a key={NAV_IDS[i]} href={`#${NAV_IDS[i]}`}>{label}</a>)}
        </nav>
        <div className="investor-header-actions">
          <div className="investor-languages" aria-label="Language / Lingua / Idioma">
            {LANGS.map(l => <Button key={l.code} variant="ghost" lang={l.code} aria-label={{en:'English',it:'Italiano',es:'Español'}[l.code]} aria-pressed={lang === l.code} onClick={() => setLang(l.code)}>{l.label}</Button>)}
          </div>
          <a className="investor-header-contact" href="#contact">{c.contact}<ArrowUpRight size={16} aria-hidden="true" /></a>
        </div>
      </div>
    </header>
    <main id="main">
      <section className="investor-hero investor-shell" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="investor-eyebrow">{c.eyebrow}</p>
          <h1 id="hero-title">{c.headline[0]}<em>{c.headline[1]}</em></h1>
          <p className="hero-intro">{c.intro}</p>
          <p className="hero-sub">{c.sub}</p>
          <div className="investor-actions">
            <Button asChild className="investor-button"><a href="#talent">{c.explore}<ArrowRight aria-hidden="true" /></a></Button>
            <a className="investor-text-link" href="#contact">{c.contact}<ArrowUpRight size={17} aria-hidden="true" /></a>
          </div>
          <p className="investor-status">{c.status}</p>
        </div>
        <ConceptFilm c={c} />
      </section>
      <div className="investor-value-strip">
        <div className="investor-shell investor-values">{c.value.map(([label, text]) => <div key={label}><p>{label}</p><strong>{text}</strong></div>)}</div>
      </div>
      <TalentJourney c={c} />
      <section className="investor-section investor-shell" id="system" aria-labelledby="system-title">
        <div className="investor-section-head"><div><p className="investor-eyebrow">{c.systemLabel}</p><h2 id="system-title">{c.systemTitle}</h2></div><p>{c.systemIntro}</p></div>
        <div className="investor-modules">{c.modules.map(([title, description], i) => {const Icon = MODULE_ICONS[i]; return <article key={title}><div className="module-top"><Icon size={26} strokeWidth={1.3} aria-hidden="true"/><span>0{i + 1}</span></div><h3>{title}</h3><p>{description}</p></article>;})}</div>
        <p className="investor-note">{c.modelNote}</p>
      </section>
      <section className="investor-qr-section" id="qr" aria-labelledby="qr-title">
        <div className="investor-shell investor-qr-grid">
          <div><p className="investor-eyebrow">{c.qrLabel}</p><h2 id="qr-title">{c.qrTitle}</h2><p className="section-intro">{c.qrIntro}</p>
            <ol className="investor-qr-list">{c.qrItems.map(([title, desc], i) => <li key={title}><span>0{i+1}</span><div><h3>{title}</h3><p>{desc}</p></div></li>)}</ol>
            <Link className="investor-text-link" to="/a/a1">{c.guestDemo}<ArrowUpRight size={18} aria-hidden="true" /></Link>
          </div>
          <div className="investor-qr-example">
            <p className="investor-eyebrow">{c.sample}</p>
            <div className="guest-preview">
              <div className="guest-preview-top"><img src="/images/5ff2283bf031.png" alt="Buskers on the Table" width="64" height="64" /><span>{c.talent}</span></div>
              <h3>{c.sampleName}</h3>
              <div className="guest-menu"><UtensilsCrossed size={22} aria-hidden="true" /><strong>{c.menu}</strong></div>
              <div className="guest-option"><Instagram size={19} aria-hidden="true" /><span>{c.profile}</span></div>
              <div className="guest-option"><CalendarHeart size={19} aria-hidden="true" /><span>{c.privateEvents}</span></div>
              <div className="guest-option"><Heart size={19} aria-hidden="true" /><span>{c.tip}</span></div>
              <div className="guest-sponsor"><span>{c.sponsorSmall}</span><strong>{c.sponsor}</strong></div>
            </div>
            <p className="investor-note">{c.qrNote}</p>
          </div>
        </div>
      </section>
      <section className="investor-section investor-shell" id="business" aria-labelledby="business-title">
        <div className="investor-section-head"><div><p className="investor-eyebrow">{c.moneyLabel}</p><h2 id="business-title">{c.moneyTitle}</h2></div><p>{c.moneyIntro}</p></div>
        <div className="investor-money">{c.money.map(([flow, title, text], i) => <article key={flow} className={i === 2 ? 'money-talent' : ''}><p className="money-flow">{flow}</p><h3>{title}</h3><p>{text}</p></article>)}</div>
        <p className="money-note">{c.moneyNote}</p>
      </section>
      <section className="investor-pilot-section" id="pilot" aria-labelledby="pilot-title">
        <div className="investor-shell investor-pilot-grid"><div><p className="investor-eyebrow">{c.pilotLabel}</p><h2 id="pilot-title">{c.pilotTitle}</h2><p className="section-intro">{c.pilotIntro}</p><div className="investor-stage"><h3>{c.stage}</h3><p>{c.stageBody}</p></div></div>
          <ol className="investor-pilot-list">{c.pilotPoints.map(([title, text], i) => <li key={title}><span>0{i+1}</span><div><h3>{title}</h3><p>{text}</p></div></li>)}</ol>
        </div>
      </section>
      <section className="investor-founder investor-shell" aria-labelledby="founder-title"><div><p className="investor-eyebrow">{c.founderLabel}</p><h2 id="founder-title">{c.founderTitle}</h2></div><div><p>{c.founderBody}</p><p className="founder-signature">{c.founderName}<span>{c.founderRole}</span><span>{c.founderLocation}</span></p></div></section>
      <section className="investor-demo investor-shell" aria-labelledby="demo-title"><p className="investor-eyebrow">{c.demoLabel}</p><h2 id="demo-title">{c.demoTitle}</h2><p className="section-intro">{c.demoText}</p><div className="investor-demo-links">{['/venue','/artist','/a/a1'].map((path,i) => <Link key={path} to={path}>{c.demoRoles[i]}<ArrowUpRight size={21} aria-hidden="true" /></Link>)}</div>
        <Accordion type="single" collapsible className="investor-gallery"><AccordionItem value="gallery"><AccordionTrigger>{c.gallery}</AccordionTrigger><AccordionContent><p className="investor-note">{c.galleryNote}</p><BusinessStory /></AccordionContent></AccordionItem></Accordion>
      </section>
      <section className="investor-contact" id="contact" aria-labelledby="contact-title"><div className="investor-shell"><p className="investor-eyebrow">{c.finalLabel}</p><h2 id="contact-title">{c.finalTitle}</h2><p>{c.finalText}</p><div className="investor-actions"><Button asChild className="investor-button"><a href={mail(c.subjectInvestor)}>{c.investorCta}<ArrowUpRight aria-hidden="true" /></a></Button><Button asChild variant="outline" className="investor-button-outline"><a href={mail(c.subjectRestaurant)}>{c.restaurantCta}<ArrowUpRight aria-hidden="true" /></a></Button></div><a href={`mailto:${EMAIL}`} className="investor-email"><Mail size={18} aria-hidden="true"/>{EMAIL}</a><span className="investor-mail-hint">{c.mailHint}</span></div></section>
    </main>
    <footer className="investor-footer investor-shell"><div><strong>BUSKERS ON THE TABLE</strong><p>{c.footer}</p></div><div><Link to="/admin">{c.admin}</Link><a href="#top">{c.back} ↑</a></div></footer>
  </div>;
}
